import React from 'react';
import BarChart from './BarChart';
import { money } from './format';

const STATUS_TEXT = { good: '✓ Good', fair: '! Watch', poor: '✕ Concern', info: 'Info', na: 'Unavailable' };

function Card({ title, status, headline, children, wide }) {
  return (
    <div className={`ins-card ${wide ? 'wide' : ''}`}>
      <div className="ins-head">
        <span className="ins-title">{title}</span>
        <span className={`ins-status ins-${status}`}>{STATUS_TEXT[status] || status}</span>
      </div>
      <div className="ins-headline">{headline}</div>
      {children}
    </div>
  );
}

function Rows({ items }) {
  return (
    <dl className="ins-rows">
      {items.filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>
      ))}
    </dl>
  );
}

function Detail({ i }) {
  const d = i.data || {};
  switch (i.code) {
    case 'sales_tracker':
      return d.months?.length ? (
        <BarChart title="Monthly sales" valueLabel="Sales" targetLabel="Target"
          rows={d.months.map(m => ({ label: m.month, value: m.sales, target: m.target }))} />
      ) : null;
    case 'profitability':
      return d.months?.length ? (
        <>
          <BarChart title="Monthly net profit" valueLabel="Net profit"
            rows={d.months.map(m => ({ label: m.month, value: m.netProfit }))} />
          <Rows items={[['12-month sales', money(d.totals.sales)], ['Gross profit', money(d.totals.grossProfit)],
            ['Net profit', money(d.totals.netProfit)], ['Gross margin', d.grossMarginPct !== null ? `${d.grossMarginPct}%` : null]]} />
        </>
      ) : null;
    case 'cash_health':
      return <Rows items={[['Cash at bank', money(d.cash)], ['Weighted outgoings', money(d.weightedOutgoings)]]} />;
    case 'corporation_tax':
      return d.years ? (
        <Rows items={d.years.map(y => [`${y.current ? 'This year to date' : y.period}`, `${money(y.tax)} on ${money(y.profit)}`])} />
      ) : null;
    case 'directors_loans':
      return d.accounts?.length ? (
        <Rows items={d.accounts.map(a => [a.account, `${money(Math.abs(a.balance))} ${a.position}`])} />
      ) : null;
    case 'business_valuation':
      return d.ebitda !== undefined ? (
        <Rows items={[['EBITDA (12 months)', money(d.ebitda)], ['Multiple', `${d.multiple}×`], ['Cash', money(d.cash)], ['Debt', money(d.debt)]]} />
      ) : null;
    case 'working_capital':
      return d.debtorDays !== undefined ? (
        <Rows items={[['Debtor days', d.debtorDays], ['Stock days', d.stockDays], ['Creditor days', d.creditorDays]]} />
      ) : null;
    case 'dividend_availability':
      return d.reserves !== undefined ? (
        <Rows items={[['Reserves brought forward', money(d.reserves)], ['Profit this year', money(d.currentYearProfit)],
          ['Less estimated corporation tax', d.corporationTax ? money(d.corporationTax) : null]]} />
      ) : null;
    case 'vat_threshold':
      return d.turnover !== undefined ? (
        <Rows items={[['Rolling 12-month turnover', money(d.turnover)], ['Threshold', money(d.threshold)]]} />
      ) : null;
    default:
      return null;
  }
}

export default function InsightsView({ result }) {
  const wide = new Set(['sales_tracker', 'profitability']);
  return (
    <div className="ins-grid">
      <Card title="Bookkeeping health" status={result.rating === 'good' ? 'good' : result.rating === 'fair' ? 'fair' : result.rating === 'poor' ? 'poor' : 'na'}
        headline={result.score === null ? '—' : `${result.score}%`}>
        <p className="ins-summary">Score from the bookkeeping checks — see the Checks tab for detail.</p>
      </Card>
      {result.insights.map(i => (
        <Card key={i.code} title={i.title} status={i.status} headline={i.headline} wide={wide.has(i.code)}>
          <p className="ins-summary">{i.summary}</p>
          <Detail i={i} />
          {i.method && <p className="ins-method">{i.method}</p>}
        </Card>
      ))}
    </div>
  );
}
